import { request } from "@umijs/max";

export type UserRole = "super_admin" | "admin" | "seat";

export interface CurrentUser {
  id: number;
  user_id?: number;
  name: string;
  email: string;
  phone?: string | null;
  email_signature_name?: string | null;
  role: UserRole;
  roles?: UserRole[];
  is_admin: boolean | number;
  is_super_admin: boolean | number;
  is_seat: boolean | number;
  status: number;
  dingding_userid?: string | null;
  dingding_unionid?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UserPayload {
  name: string;
  email: string;
  phone: string;
  password?: string;
  email_signature_name?: string | null;
  roles: UserRole[];
  status: number;
  dingding_userid: string | null;
  dingding_unionid: string | null;
}

export interface DingTalkCandidate {
  userid: string;
  unionid: string;
  name: string;
  mobile?: string;
  email?: string;
}

export interface UserListParams {
  page?: number;
  per_page?: number;
  name?: string;
  email?: string;
  role?: UserRole;
  status?: number;
}

export interface ApiResponse<T> {
  code: number;
  message?: string;
  data: T;
}

export interface LoginData {
  user: CurrentUser;
  token: string;
  expires_in?: number;
}

/** 使用邮箱和密码登录，认证失败仅由登录页展示，不撤销其他已登录会话。 */
export async function login(data: { email: string; password: string }) {
  return request<ApiResponse<LoginData>>("/auth/login", {
    method: "POST",
    data,
    skipErrorHandler: true,
    skipAuthRedirect: true,
  });
}

/** 注销当前登录凭证。 */
export async function logout() {
  return request("/auth/logout", { method: "POST" });
}

/** 获取服务端缓存中的当前用户资料和权限。 */
export async function userInfo() {
  return request<ApiResponse<{ user: CurrentUser }>>("/auth/me", {
    method: "GET",
  });
}

/** 获取可参与邮件分配的坐席用户。 */
export async function getSeatUserList() {
  return request("/users/seat-users", { method: "GET" });
}

/** 获取邮件查询使用的用户选项。 */
export async function getEmailSearchUserList() {
  return request("/users/email-search-users", { method: "GET" });
}

/** 分页查询用户列表，保留禁用状态 0 的筛选值。 */
export async function getUsers(params: UserListParams) {
  return request<
    ApiResponse<{
      list: CurrentUser[];
      meta: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
      };
    }>
  >("/users", { method: "GET", params, skipErrorHandler: true });
}

/** 创建用户并返回保存后的资料。 */
export async function createUser(data: UserPayload) {
  return request<ApiResponse<{ user: CurrentUser }>>("/users", {
    method: "POST",
    data,
    skipErrorHandler: true,
  });
}

/** 更新用户；未提交密码时保留原密码和现有登录凭证。 */
export async function updateUser(id: number, data: UserPayload) {
  return request<ApiResponse<{ user: CurrentUser }>>(`/users/${id}`, {
    method: "PUT",
    data,
    skipErrorHandler: true,
  });
}

/** 按姓名获取可绑定的钉钉用户候选项。 */
export async function searchDingTalkUsers(name: string) {
  return request<ApiResponse<DingTalkCandidate[]>>("/users/dingtalk-users", {
    method: "GET",
    params: { name },
    skipErrorHandler: true,
  });
}

/** 获取公开登录配置，失败不影响当前会话，不向浏览器暴露应用密钥。 */
export async function getDingTalkConfig() {
  return request<ApiResponse<{ enabled: boolean }>>("/auth/dingtalk/config", {
    method: "GET",
    skipErrorHandler: true,
    skipAuthRedirect: true,
  });
}

/** 由服务端生成授权地址与一次性状态，失败不触发已有会话退出。 */
export async function getDingTalkAuthorize() {
  return request<ApiResponse<{ url: string; state: string }>>(
    "/auth/dingtalk/authorize",
    {
      method: "GET",
      skipErrorHandler: true,
      skipAuthRedirect: true,
    }
  );
}

/** 提交授权码与状态，交换失败交由登录页处理，不能清除刚签发的新凭证。 */
export async function loginWithDingTalk(data: { code: string; state: string }) {
  return request<ApiResponse<LoginData>>("/auth/dingtalk/login", {
    method: "POST",
    data,
    skipErrorHandler: true,
    skipAuthRedirect: true,
  });
}

/** 提取服务端字段校验错误，网络异常时使用明确的后备提示。 */
export function getUserErrorMessage(error: unknown, fallback: string): string {
  const response = (
    error as {
      response?: {
        data?: { message?: string; errors?: Record<string, string[]> };
      };
    }
  )?.response?.data;
  const fieldError = Object.values(response?.errors || {})[0]?.[0];
  return fieldError || response?.message || fallback;
}
